import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { Sun, Moon, LogOut } from "lucide-react"
import { PASSWORD_POLICY_RULES, evaluatePasswordPolicy } from "@mc/types"
import { useAuth } from "../auth/AuthProvider.js"
import { useTheme } from "../theme/ThemeProvider.js"
import { updateProfile, checkUsernameAvailable } from "../api/profile.js"
import { friendlyError } from "../api/errorMessages.js"
import { Button } from "../components/ui/Button.js"
import { Input } from "../components/ui/Input.js"
import { useToast } from "../components/ui/Toast.js"
import { AvatarPicker } from "../components/ui/AvatarPicker.js"

const USERNAME_CHECK_DEBOUNCE_MS = 400

type ProfileTab = "personalizacion" | "seguridad" | "danger"

const TABS: { id: ProfileTab; label: string }[] = [
  { id: "personalizacion", label: "Personalización" },
  { id: "seguridad", label: "Seguridad" },
  { id: "danger", label: "Danger Zone" },
]

/**
 * `/profile` (FR section 12), reorganizado en subsecciones (Fase C del
 * TODO de UI/UX). Tabs con useState, mismo patron visual que
 * ServerTabsLayout.tsx (clases mc-server-tabs/mc-server-tab) pero sin
 * rutas propias — son subsecciones de una sola pagina, no vistas
 * navegables por URL.
 *
 * Nota (comentario, no UI): una futura seccion "Actividad" (stats de
 * conexion, sesiones activas, audit log personal) iria aca como cuarto
 * tab — no se crea todavia por no tener contenido real que mostrar.
 */
export function ProfilePage() {
  const [tab, setTab] = useState<ProfileTab>("personalizacion")

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)", maxWidth: 480 }}>
      <div>
        <h1 style={{ fontSize: "var(--text-3xl)" }}>Perfil</h1>
        <p style={{ color: "var(--text-muted)" }}>Tu nombre, usuario, avatar, contraseña y cuenta.</p>
      </div>

      <nav className="mc-server-tabs" aria-label="Secciones de perfil">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className="mc-server-tab"
            aria-current={tab === t.id ? "page" : undefined}
            style={{ border: "none", background: "transparent", cursor: "pointer" }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "personalizacion" && <PersonalizacionSection />}
      {tab === "seguridad" && <SeguridadSection />}
      {tab === "danger" && <DangerZoneSection />}

      <MobileAccountControls />
    </div>
  )
}

/**
 * Fase B del TODO de UI/UX (hueco real dejado pendiente): en desktop,
 * tema y logout viven en el footer del Sidebar (SidebarUserFooter). En
 * mobile no hay Sidebar, y BottomNav es solo tabs de navegacion, asi
 * que esos dos controles no tenian forma de alcanzarse. `layout.css`
 * ya documentaba la decision de que vivieran aca via `.mc-mobile-only`
 * (oculta a partir de 1024px, donde el Sidebar ya los cubre) — esta es
 * la implementacion real de esa clase, que hasta ahora no se usaba en
 * ningun lugar del codigo.
 */
function MobileAccountControls() {
  const { logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const navigate = useNavigate()
  const [loggingOut, setLoggingOut] = useState(false)

  const handleLogout = async () => {
    setLoggingOut(true)
    await logout()
    navigate("/login")
  }

  return (
    <section
      className="mc-mobile-only"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-2)",
        borderTop: "1px solid var(--border-default)",
        paddingTop: "var(--space-4)",
      }}
    >
      <button
        type="button"
        onClick={toggleTheme}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-3)",
          padding: "var(--space-2) var(--space-3)",
          background: "transparent",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-sm)",
          color: "var(--text-primary)",
          fontSize: "var(--text-sm)",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        {theme === "dark" ? <Sun size={16} aria-hidden /> : <Moon size={16} aria-hidden />}
        <span>{theme === "dark" ? "Tema claro" : "Tema oscuro"}</span>
      </button>
      <button
        type="button"
        disabled={loggingOut}
        onClick={handleLogout}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-3)",
          padding: "var(--space-2) var(--space-3)",
          background: "transparent",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-sm)",
          color: "var(--status-error)",
          fontSize: "var(--text-sm)",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <LogOut size={16} aria-hidden />
        <span>{loggingOut ? "Cerrando sesión…" : "Cerrar sesión"}</span>
      </button>
    </section>
  )
}

function PersonalizacionSection() {
  const { user } = useAuth()
  const toast = useToast()

  const [displayName, setDisplayName] = useState(user?.displayName ?? "")
  const [username, setUsername] = useState(user?.username ?? "")
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? "")
  const [usernameStatus, setUsernameStatus] = useState<"idle" | "checking" | "available" | "taken">("idle")
  const [savingIdentity, setSavingIdentity] = useState(false)

  useEffect(() => {
    if (!user || username === user.username || username.length < 3) {
      setUsernameStatus("idle")
      return
    }
    setUsernameStatus("checking")
    const timer = setTimeout(() => {
      checkUsernameAvailable(username)
        .then(({ available }) => setUsernameStatus(available ? "available" : "taken"))
        .catch(() => setUsernameStatus("idle"))
    }, USERNAME_CHECK_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [username, user])

  if (!user) return null

  const identityDirty =
    displayName !== (user.displayName ?? "") || username !== user.username || avatarUrl !== (user.avatarUrl ?? "")

  const handleSaveIdentity = async () => {
    setSavingIdentity(true)
    try {
      await updateProfile({
        displayName: displayName || null,
        username: username !== user.username ? username : undefined,
        avatarUrl: avatarUrl || null,
      })
      toast.show("Perfil actualizado.", "success")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setSavingIdentity(false)
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <Input label="Nombre para mostrar" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      <div>
        <Input
          label="Usuario"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          minLength={3}
          maxLength={32}
          error={usernameStatus === "taken" ? "Ese nombre de usuario ya está en uso." : undefined}
        />
        {usernameStatus === "checking" && (
          <span style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>Comprobando disponibilidad…</span>
        )}
        {usernameStatus === "available" && (
          <span style={{ fontSize: "var(--text-xs)", color: "var(--status-success)" }}>Disponible.</span>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        <span style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-medium)" }}>Avatar</span>
        <AvatarPicker value={avatarUrl} onChange={setAvatarUrl} />
        <Input
          label="URL del avatar"
          value={avatarUrl}
          onChange={(e) => setAvatarUrl(e.target.value)}
          placeholder="https://…"
        />
        <Button variant="secondary" size="sm" disabled title="Próximamente" style={{ alignSelf: "flex-start" }}>
          Subir archivo
        </Button>
      </div>

      <Button
        loading={savingIdentity}
        disabled={!identityDirty || usernameStatus === "taken" || usernameStatus === "checking"}
        onClick={handleSaveIdentity}
        style={{ alignSelf: "flex-start" }}
      >
        Guardar
      </Button>
    </section>
  )
}

function SeguridadSection() {
  const toast = useToast()

  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [savingPassword, setSavingPassword] = useState(false)

  const ruleResults = evaluatePasswordPolicy(newPassword)
  const allRulesPass = ruleResults.every((r) => r.passed)
  const passwordTouched = newPassword.length > 0

  const handleChangePassword = async () => {
    setSavingPassword(true)
    try {
      await updateProfile({ currentPassword, newPassword })
      toast.show("Contraseña actualizada.", "success")
      setCurrentPassword("")
      setNewPassword("")
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setSavingPassword(false)
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      <h2 style={{ fontSize: "var(--text-2xl)" }}>Cambiar contraseña</h2>
      <Input
        label="Contraseña actual"
        type="password"
        value={currentPassword}
        onChange={(e) => setCurrentPassword(e.target.value)}
        autoComplete="current-password"
      />
      <Input
        label="Contraseña nueva"
        type="password"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        autoComplete="new-password"
      />
      {passwordTouched && (
        <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
          {PASSWORD_POLICY_RULES.map((rule) => {
            const passed = ruleResults.find((r) => r.ruleId === rule.id)?.passed ?? false
            return (
              <li
                key={rule.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "var(--space-2)",
                  fontSize: "var(--text-xs)",
                  color: passed ? "var(--status-success)" : "var(--text-muted)",
                  transition: "color var(--duration-small) var(--ease-out)",
                }}
              >
                <span aria-hidden>{passed ? "✓" : "○"}</span>
                {rule.label}
              </li>
            )
          })}
        </ul>
      )}
      <Button
        variant="secondary"
        loading={savingPassword}
        disabled={!currentPassword || !passwordTouched || !allRulesPass}
        onClick={handleChangePassword}
        style={{ alignSelf: "flex-start" }}
      >
        Actualizar contraseña
      </Button>
    </section>
  )
}

/**
 * Placeholder deliberado (Fase C del TODO): el borrado de cuenta propia
 * necesita una decision de producto de Joanfer sobre que pasa con
 * servers/families que la cuenta posee (¿se transfieren? ¿se bloquea si
 * tiene servidores activos?) antes de implementarse. Ver "Fuera de
 * alcance" en TODO.md.
 */
function DangerZoneSection() {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <h2 style={{ fontSize: "var(--text-2xl)", color: "var(--status-error)" }}>Danger Zone</h2>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>
        Próximamente: eliminar cuenta.
      </p>
    </section>
  )
}
